<template>
  <span
    ref="element"
    v-on-long-press.prevent.capture="() => triggerAdvanced()"
    class="tag is-rounded tag-label-v"
    :class="sizeClass"
    @mousedown="undefer()"
    @mouseup="() => undefer()"
    @touchstart="enableTouchSelect(false)"
    @touchend="enableTouchSelect(true)"
  >
    <a v-if="canClick" class="name-v" href="#" @click.prevent="triggerClick()">
      {{ name }}
    </a>
    <span v-else class="name-v">{{ name }}</span>

    <button
      v-if="canRemove"
      class="delete is-small"
      @click.prevent="emit('remove', serializedTag)"
    ></button>
  </span>
</template>

<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import colorParse from "color-parse";
  import { vOnLongPress } from "@vueuse/components";

  const props = defineProps({
    tagId: { type: String, default: null },
    name: { type: String, required: true },
    color: { type: String, required: true },
    canRemove: { type: Boolean, required: false, default: true },
    canClick: { type: Boolean, required: false, default: false },
    size: { type: String, required: false, default: "medium" },
  });

  const emit = defineEmits(["tag-click", "remove", "advanced"]);

  const element = _ref(null);
  let clickDeferred = _ref(false);

  const triggerAdvanced = () => {
    clickDeferred.value = true;
    emit("advanced", serializedTag.value);
  };

  const undefer = () => {
    clickDeferred.value = false;
  };

  const triggerClick = () => {
    if (clickDeferred.value) {
      clickDeferred.value = false;
      return;
    }

    emit("tag-click", serializedTag.value);
  };

  const enableTouchSelect = (enabled) => {
    document.body.classList.toggle("unselect-for-tag-label", !enabled);
  };

  const serializedTag = _computed(() => {
    return {
      id: props.tagId,
      name: props.name,
      color: props.color,
    };
  });

  const sizeClass = _computed(() => {
    /* Don't construct CSS classes re: PurgeCSS */
    const mapping = {
      small: "is-small",
      medium: "is-medium",
      large: "is-large",
    };
    return mapping[props.size];
  });

  const luminance = (r, g, b) => {
    /* This has been pieced together based on a few perceptual color formulas on the web. */
    const scaled = Object.entries({ r, g, b }).map(([channelName, c]) => {
      c /= 255;
      c = c > 0.03928 ? Math.pow((c + 0.055) / 1.055, 2.4) : (c /= 12.92);
      return [channelName, c];
    });

    const rgb = Object.fromEntries(scaled);
    return 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
  };

  const contrastForPair = (lum1, lum2) => {
    return (Math.max(lum1, lum2) + 0.05) / (Math.min(lum1, lum2) + 0.05);
  };

  const foregroundColor = _computed(() => {
    const { space, values } = colorParse(props.color);
    if (space !== "rgb") {
      throw `Invalid Color Space: ${space}`;
    }

    const backgroundLuminance = luminance(...values);
    const whiteContrast = contrastForPair(backgroundLuminance, luminance(0xee, 0xee, 0xee));

    /* Prefer white text for aesthetics, but go to black when it's unreadable.  Threshold chosen by
       "eyeball observation" */
    return whiteContrast >= 1.7 ? "#eee" : "#222";
  });
</script>

<style lang="scss">
  body.unselect-for-tag-label {
    user-select: none;
    -webkit-user-select: none;
  }
</style>

<style lang="scss" scoped>
  @use "sass:color";
  @import "~jose_modern/css";

  .tag-label-v {
    background-color: v-bind(color);
    cursor: default;
    -webkit-touch-callout: none;

    .name-v {
      color: v-bind(foregroundColor);
      flex-grow: 1;
    }

    a.name-v {
      cursor: pointer;
      -webkit-touch-callout: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
</style>
